/**
 * @module Model
 */

import { CustomFieldValue } from '@app/components/custom-fields/shared/custom-field.model';
import { OrderBulkAction, OrderStatusType, OrderLineItem, VendorLineItem } from '@feature/orders/shared';
import { OrderUser } from '@feature/users/shared';

/**
 * Class representing an order in the application.
 *
 * This class encapsulates various properties related to an order, including identifiers,
 * payer and payee information, financial details, and associated metadata. It is used to manage
 * and represent order data within the application.
 *
 * @class
 * @model
 */
export class Order {
	/**
	 * The unique identifier for the order.
	 * @type {number}
	 */
	id: number;

	/**
	 * Deferred number associated with the order (optional).
	 * @type {string | undefined}
	 */
	deferredNumber?: string;

	/**
	 * The order number.
	 * @type {number}
	 */
	number: number;

	/**
	 * The title of the order.
	 * @type {string}
	 */
	title: string;

	/**
	 * The unique identifier for the payer.
	 * @type {number}
	 */
	payerId: number;

	/**
	 * Unique identifier for the payer's company (optional).
	 * @type {number}
	 */
	payerCompanyId?: number;

	/**
	 * Name of the payer's company (optional).
	 * @type {string}
	 */
	payerCompanyName?: string;

	/**
	 * Details of the payer user.
	 * @note User is optional as it is not available during the order creation.
	 * @type {OrderUser | undefined}
	 */
	payerDetails?: OrderUser;

	/**
	 * Full name of the payer (optional).
	 * @type {string | undefined}
	 */
	payerFullName?: string;

	/**
	 * Unique identifier for the signatory associated with the payer (optional).
	 *
	 * @note ETT-526 Allow an optional payer signatory id to be sent for the current payer context.
	 *
	 * @type {number}
	 */
	payerSignatoryId?: string;

	/**
	 * The unique identifier for the payee.
	 * @type {number}
	 */
	payeeId: number;

	/**
	 * Full name of the payee (optional).
	 * @type {string | undefined}
	 */
	payeeFullName?: string;

	/**
	 * Unique identifier for the payee's company (optional).
	 * @type {number | undefined}
	 */
	payeeCompanyId?: number;

	/**
	 * Name of the payee's company (optional).
	 * @type {string | undefined}
	 */
	payeeCompanyName?: string;

	/**
	 * Details of the payee user.
	 * @note User is optional as it is not available during the order creation.
	 * @type {OrderUser | undefined}
	 */
	payeeDetails?: OrderUser;

	/**
	 * Unique identifier for the agent (optional).
	 * @type {number | undefined}
	 */
	agentId?: number;

	/**
	 * Details of the agent user.
	 * @note User is optional as it is not available during the order creation.
	 * @type {OrderUser | undefined}
	 */
	agentDetails?: OrderUser;

	/**
	 * Unique identifier for the agency (optional).
	 * @type {number | undefined}
	 */
	agencyId?: number;

	/**
	 * Name of the agency (optional).
	 * @type {string | undefined}
	 */
	agencyName?: string;

	/**
	 * The unique identifier for the template associated with the order.
	 * @type {number}
	 */
	templateId: number;

	/**
	 * Prefix for the template associated with the order (optional).
	 * @type {string | undefined}
	 */
	templatePrefix?: string;

	/**
	 * Currency code for the country associated with the order (optional).
	 * @type {string | undefined}
	 */
	countryCurrencyCode?: string;

	/**
	 * Currency symbol for the country associated with the order (optional).
	 * @type {string | undefined}
	 */
	countryCurrencySymbol?: string;

	/**
	 * Unique identifier for the country associated with the order (optional).
	 * @type {number | undefined}
	 */
	countryId?: number;

	/**
	 * Unique identifier for the state associated with the order (optional).
	 *
	 * @note ETT-526 Allow an optional state id to be sent for the existing recipient.
	 *
	 * @type {number}
	 */
	stateId?: number;

	/**
	 * Memo displayed for the order (optional).
	 * @type {string | undefined}
	 */
	memoDisplayed?: string;

	/**
	 * Memo that is optional for the order.
	 * @type {string | undefined}
	 */
	memoOptional?: string;

	/**
	 * The number of hours spent on the order.
	 * @type {number}
	 */
	quantity: number;

	/**
	 * The rate amount spent on the order.
	 * @type {number}
	 */
	rate: number;

	/**
	 * The effective rate amount that is used in case of the flat rate calculation.
	 * @type {number}
	 */
	effectiveRate: number;

	/**
	 * Indicates whether the order uses a flat rate calculation.
	 * @type {boolean}
	 */
	isFlatRateCalculation: boolean;

	/**
	 * Indicates whether the order is a lump sum payment.
	 * @type {boolean}
	 */
	isLumpSumCalculation: boolean;

	/**
	 * Indicates whether the order has been migrated.
	 * @type {boolean}
	 */
	migrated: boolean;

	/**
	 * Indicates whether the order is deferred.
	 * @type {boolean}
	 */
	deferred: boolean;

	/**
	 * The total quantity of items in the order.
	 * @type {number}
	 */
	totalQuantity: number;

	/**
	 * The total amount for the order.
	 * @type {number}
	 */
	totalAmount: number;

	/**
	 * The subtotal amount to be paid to the payee.
	 * @type {number}
	 */
	subtotalToPayee: number;

	/**
	 * Amount associated with the payment gateway (optional).
	 * @type {number | undefined}
	 */
	paymentGatewayAmount?: number;

	/**
	 * The exchange total order amount associated with the recipient's international money transfer (optional).
	 * @type {number | undefined}
	 */
	convertedTotalAmount?: number;

	/**
	 * The exchange rate associated with the recipient's international money transfer (optional).
	 * @type {number | undefined}
	 */
	conversionRate?: number;

	/**
	 * The exchange currency code associated with the recipient's international money transfer (e.g. USD, CAD etc.) (optional).
	 * @type {string | undefined}
	 */
	conversionCurrencyCode?: string;

	/**
	 * The start date of the order.
	 * @type {string | Date}
	 */
	startDate: string | Date;

	/**
	 * The finish date of the order.
	 * @type {string | Date}
	 */
	finishDate: string | Date;

	/**
	 * The date when the order was created.
	 * @type {Date}
	 */
	createdDate: Date;

	/**
	 * Array of statuses associated with the order (optional).
	 * @type {Array<OrderStatus> | undefined}
	 */
	statuses?: Array<OrderStatus>;

	/**
	 * Array of line items associated with the order (optional).
	 * @type {Array<OrderLineItem> | undefined}
	 */
	lineItems?: Array<OrderLineItem>;

	/**
	 * Array of vendor line items associated with the order (optional).
	 * @type {Array<VendorLineItem> | undefined}
	 */
	vendorLineItems?: Array<VendorLineItem>;

	/**
	 * Array of custom field values associated with the order (optional).
	 * @type {Array<CustomFieldValue> | undefined}
	 */
	customFieldValues?: Array<CustomFieldValue>;

	/**
	 * Flag indicating whether the order has been checked (optional).
	 * @note Flag is mostly used with bulk actions.
	 * @type {boolean | undefined}
	 */
	checked?: boolean;

	/**
	 * Verification query for the deferred order (optional).
	 * @type {string}
	 */
	verificationQuery?: string;

	/**
	 * Flag indicating whether the order has been deleted.
	 * @note Flag is mostly used with bulk actions or for the search.
	 * @type {boolean | undefined}
	 */
	isDeleted?: boolean;

	/**
	 * Calculates the total hours based on the hours and minutes from the order line items import details.
	 * @returns {number} The calculated total vendor hours.
	 */
	get vendorLineItemsQtyTotal(): number {
		if (this.vendorLineItems == null) return 0;
		return this.vendorLineItems.reduce((total: number, vendorLineItem: VendorLineItem) => {
			return total + parseFloat(vendorLineItem?.quantity?.toString());
		}, 0);
	}

	/**
	 * Calculates the total rate based on the rate from the order line items import details.
	 * @returns {number} The calculated total vendor rate.
	 */
	get vendorLineItemsAmountTotal(): number {
		if (this.vendorLineItems == null) return 0;
		return this.vendorLineItems.reduce((total: number, vendorLineItem: VendorLineItem) => {
			return total + (parseFloat(vendorLineItem?.quantity?.toString()) * parseFloat(vendorLineItem?.rate?.toString()));
		}, 0);
	}
}

/**
 * Class representing the status of an order in the application.
 *
 * @class
 * @model
 */
export class OrderStatus {
	/**
	 * The unique identifier for the order associated with this status.
	 * @type {number}
	 */
	orderId: number;

	/**
	 * The unique status identifier.
	 * @type {number}
	 */
	statusId: number;

	/**
	 * The type of the order status.
	 * @type {OrderStatusType}
	 */
	statusType: OrderStatusType;

	/**
	 * The unique alias of the order status.
	 * @type {string}
	 */
	statusAlias: string;

	/**
	 * The unique display name of the order status.
	 * @type {string}
	 */
	statusName: string;

	/**
	 * A description or explanation of the order status.
	 * @type {string}
	 */
	explanation: string;
}

/**
 * Class representing the input for bulk actions on orders.
 *
 * This class encapsulates the details of a bulk action to be performed on a list of orders,
 * including the specific action type and the array of order IDs that the action will apply to.
 *
 * @class
 * @model
 */
export class OrderBulkActionInput {
	/**
	 * The action to be performed on the orders.
	 * @type {OrderBulkAction}
	 */
	action: OrderBulkAction;

	/**
	 * An array of order IDs that the bulk action will apply to.
	 * @type {Array<number>}
	 */
	orders: Array<number>;

	/**
	 * Verification query associated with the bulk deferred orders (optional).
	 *
	 * @note Presumably, the correct approach should be to show multiple textarea inputs associated with a selected order.
	 *
	 * @type {string}
	 */
	verificationQuery?: string;
}

/**
 * Class representing the output of a bulk action on orders.
 *
 * This class encapsulates the results of performing a bulk action, including the lists of
 * affected and unaffected orders.
 *
 * @class
 * @model
 */
export class OrderBulkActionOutput {
	/**
	 * An array of orders that were successfully affected by the bulk action.
	 * @type {Array<Order>}
	 */
	affectedOrders: Array<Order>;

	/**
	 * An array of orders that were not affected by the bulk action, along with error messages.
	 * @type {Array<UnaffectedOrder>}
	 */
	unaffectedOrders: Array<UnaffectedOrder>;
}

/**
 * Class representing an order that was not affected by a bulk action.
 *
 * This class includes the ID of the order and an error message explaining why it was not affected.
 *
 * @class
 * @model
 */
export class UnaffectedOrder {
	/**
	 * The unique identifier for the order that was not affected.
	 * @type {number}
	 */
	orderId: number;

	/**
	 * A message explaining the reason the order was not affected.
	 * @type {string}
	 */
	errorMsg: string;
}

/** Action required notifications data */
export class OrderCount {
	count: number;
}
